import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import { axiosPrivate } from '@/api/axios';
import { USER_API_PATH } from '@/lib/constants';
import { KYC_OPTIONS } from '@/lib/kycOptions';
import { ChipSelect } from '@/components/domain/kyc/ChipSelect';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import type { KycData } from '@/types/auth';

const STEP_TITLES = [
  'Tu empresa',
  'Servicios y vehículos',
  'Situación actual',
  'Implementación',
];

export default function Kyc() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Initialize KYC state from currentUser
  const [kyc, setKyc] = useState<KycData>(() => ({
    ...currentUser?.kyc,
    lookingToSolve: currentUser?.kyc?.lookingToSolve || [],
    servicesTypes: currentUser?.kyc?.servicesTypes || [],
    vehiclesTypes: currentUser?.kyc?.vehiclesTypes || [],
    currentManagement: currentUser?.kyc?.currentManagement || [],
    currentProblems: currentUser?.kyc?.currentProblems || [],
    clientTypes: currentUser?.kyc?.clientTypes || [],
    communicationMethods: currentUser?.kyc?.communicationMethods || [],
    consultingNeeds: currentUser?.kyc?.consultingNeeds || [],
  }));
  const [zip, setZip] = useState(currentUser?.zip || '');

  // Detect read-only (all required KYC fields complete)
  const isReadOnly = !!(
    kyc.position && kyc.servicesTypes?.length && kyc.vehiclesTypes?.length &&
    kyc.hasMultipleBranches && kyc.clientTypes?.length &&
    kyc.communicationMethods?.length && kyc.hasDefinedProcesses &&
    kyc.hasTesterReady && kyc.implementationTimeline
  );

  // Set a single-value field
  const setField = useCallback((field: keyof KycData, value: string) => {
    if (isReadOnly) return;
    setKyc(prev => ({ ...prev, [field]: value }));
  }, [isReadOnly]);

  // Toggle an item in an array field
  const toggleArrayField = useCallback((field: keyof KycData, item: string) => {
    if (isReadOnly) return;
    setKyc(prev => {
      const arr = (prev[field] as string[]) || [];
      const idx = arr.indexOf(item);
      if (idx !== -1) {
        return { ...prev, [field]: arr.filter((_, i) => i !== idx) };
      }
      return { ...prev, [field]: [...arr, item] };
    });
  }, [isReadOnly]);

  // Step validity
  const isStepValid = useCallback((): boolean => {
    switch (currentStep) {
      case 1: {
        const base = !!(kyc.position && kyc.employeesQuantity && kyc.hasAssistant &&
          zip && /^\d{5}$|^\d{5}-\d{4}$/.test(zip) && kyc.hasMultipleBranches);
        if (base && kyc.hasMultipleBranches === KYC_OPTIONS.hasMultipleBranches[0]) {
          return !!kyc.branchCount;
        }
        return base;
      }
      case 2:
        return !!(kyc.servicesTypes?.length && kyc.vehiclesTypes?.length &&
          kyc.weeklyServices && kyc.clientTypes?.length);
      case 3: {
        const base = !!(kyc.communicationMethods?.length && kyc.currentManagement?.length &&
          kyc.currentProblems?.length && kyc.lookingToSolve?.length);
        if (base && kyc.currentManagement?.includes(KYC_OPTIONS.currentManagement[2])) {
          return !!kyc.currentManagementApp;
        }
        return base;
      }
      case 4: {
        const base = !!(kyc.hasDefinedProcesses && kyc.hasTesterReady && kyc.implementationTimeline);
        if (base && kyc.hasDefinedProcesses === KYC_OPTIONS.hasDefinedProcesses[1]) {
          return !!(kyc.consultingNeeds?.length);
        }
        return base;
      }
      default:
        return false;
    }
  }, [currentStep, kyc, zip]);

  const nextStep = () => {
    if (!isStepValid() || currentStep >= 4) return;
    setCurrentStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    if (currentStep <= 1) return;
    setCurrentStep(s => s - 1);
    window.scrollTo(0, 0);
  };

  const save = async () => {
    if (saving || !isStepValid()) return;
    setSaving(true);

    try {
      const userId = currentUser?.id;
      await axiosPrivate.patch(`/${USER_API_PATH}/${userId}`, {
        calculateKyc: true,
        kyc,
        zip,
      });
      navigate('/auth/welcome', { replace: true });
    } catch (err) {
      console.error('[KYC save error]', err);
      setSaving(false);
    }
  };

  // Single-select chip helper
  const renderSingleSelect = (field: keyof KycData, options: readonly string[]) => (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          disabled={isReadOnly}
          onClick={() => setField(field, opt)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            kyc[field] === opt
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white text-slate-600 border-slate-300 hover:border-blue-300'
          } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  return (
    <div className="container mx-auto px-4 h-full">
      <div className="flex content-center items-center justify-center h-full py-8">
        <div className="w-full lg:w-8/12 px-4">
          <Card variant="legacy" className="mb-6">
            <div className="px-6 py-6">
              {/* Step indicator */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h6 className="text-slate-500 text-sm font-bold">
                    Paso {currentStep} de 4: {STEP_TITLES[currentStep - 1]}
                  </h6>
                  <span className="text-xs text-slate-400">{Math.round(currentStep / 4 * 100)}%</span>
                </div>
                <Progress value={currentStep / 4 * 100} className="h-2" />
              </div>

              {/* Step 1: Company info */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label>Qué posición tienes en la empresa?</Label>
                    {renderSingleSelect('position', KYC_OPTIONS.position)}
                  </div>
                  <div>
                    <Label>Cuántos empleados son?</Label>
                    {renderSingleSelect('employeesQuantity', KYC_OPTIONS.employeesQuantity)}
                  </div>
                  <div>
                    <Label>Cuentas con asistente administrativo?</Label>
                    {renderSingleSelect('hasAssistant', KYC_OPTIONS.hasAssistant)}
                  </div>
                  <div>
                    <Label htmlFor="zip">Código postal</Label>
                    <Input
                      id="zip"
                      value={zip}
                      onChange={e => setZip(e.target.value)}
                      placeholder="12345"
                      disabled={isReadOnly}
                      className="max-w-xs"
                    />
                    {zip && !/^\d{5}$|^\d{5}-\d{4}$/.test(zip) && (
                      <p className="text-red-500 text-xs mt-1">Código postal inválido</p>
                    )}
                  </div>
                  <div>
                    <Label>Tienes varias sucursales?</Label>
                    {renderSingleSelect('hasMultipleBranches', KYC_OPTIONS.hasMultipleBranches)}
                  </div>
                  {kyc.hasMultipleBranches === KYC_OPTIONS.hasMultipleBranches[0] && (
                    <div>
                      <Label>Cuántas sucursales?</Label>
                      {renderSingleSelect('branchCount', KYC_OPTIONS.branchCount)}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Services & vehicles */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <Label>Qué tipo de servicios ofrece tu taller?</Label>
                    <ChipSelect
                      options={KYC_OPTIONS.servicesTypes}
                      selected={kyc.servicesTypes || []}
                      onToggle={(item) => toggleArrayField('servicesTypes', item)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <Label>A qué tipo de vehículos das servicio?</Label>
                    <ChipSelect
                      options={KYC_OPTIONS.vehiclesTypes}
                      selected={kyc.vehiclesTypes || []}
                      onToggle={(item) => toggleArrayField('vehiclesTypes', item)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <Label>Cuántos servicios realizas a la semana?</Label>
                    {renderSingleSelect('weeklyServices', KYC_OPTIONS.weeklyServices)}
                  </div>
                  <div>
                    <Label>Tipo de clientes</Label>
                    <ChipSelect
                      options={KYC_OPTIONS.clientTypes}
                      selected={kyc.clientTypes || []}
                      onToggle={(item) => toggleArrayField('clientTypes', item)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Current situation */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <Label>Métodos de comunicación con clientes</Label>
                    <ChipSelect
                      options={KYC_OPTIONS.communicationMethods}
                      selected={kyc.communicationMethods || []}
                      onToggle={(item) => toggleArrayField('communicationMethods', item)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <Label>Hoy cómo gestionas tu taller?</Label>
                    <ChipSelect
                      options={KYC_OPTIONS.currentManagement}
                      selected={kyc.currentManagement || []}
                      onToggle={(item) => toggleArrayField('currentManagement', item)}
                      disabled={isReadOnly}
                    />
                  </div>
                  {kyc.currentManagement?.includes(KYC_OPTIONS.currentManagement[2]) && (
                    <div>
                      <Label htmlFor="currentManagementApp">Qué app o software usas?</Label>
                      <Input
                        id="currentManagementApp"
                        value={kyc.currentManagementApp || ''}
                        onChange={e => setField('currentManagementApp' as any, e.target.value)}
                        disabled={isReadOnly}
                        className="max-w-xs"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Principales problemas actuales</Label>
                    <ChipSelect
                      options={KYC_OPTIONS.currentProblems}
                      selected={kyc.currentProblems || []}
                      onToggle={(item) => toggleArrayField('currentProblems', item)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div>
                    <Label>Qué buscas resolver?</Label>
                    <ChipSelect
                      options={KYC_OPTIONS.lookingToSolve}
                      selected={kyc.lookingToSolve || []}
                      onToggle={(item) => toggleArrayField('lookingToSolve', item)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Implementation */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div>
                    <Label>Tienes procesos definidos?</Label>
                    <div className="flex flex-wrap gap-2">
                      {KYC_OPTIONS.hasDefinedProcesses.map(opt => (
                        <button
                          key={opt}
                          type="button"
                          disabled={isReadOnly}
                          onClick={() => {
                            setField('hasDefinedProcesses', opt);
                            // Clear consulting needs if processes are defined
                            if (opt === KYC_OPTIONS.hasDefinedProcesses[0]) {
                              setKyc(prev => ({ ...prev, consultingNeeds: [] }));
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            kyc.hasDefinedProcesses === opt
                              ? 'bg-blue-500 text-white border-blue-500'
                              : 'bg-white text-slate-600 border-slate-300 hover:border-blue-300'
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  {kyc.hasDefinedProcesses === KYC_OPTIONS.hasDefinedProcesses[1] && (
                    <div>
                      <Label>En qué necesitas consultoría?</Label>
                      <ChipSelect
                        options={KYC_OPTIONS.consultingNeeds}
                        selected={kyc.consultingNeeds || []}
                        onToggle={(item) => toggleArrayField('consultingNeeds', item)}
                        disabled={isReadOnly}
                      />
                    </div>
                  )}
                  <div>
                    <Label>Tu equipo está listo para probar?</Label>
                    {renderSingleSelect('hasTesterReady', KYC_OPTIONS.hasTesterReady)}
                  </div>
                  <div>
                    <Label>Línea de tiempo de implementación</Label>
                    {renderSingleSelect('implementationTimeline', KYC_OPTIONS.implementationTimeline)}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                >
                  Anterior
                </Button>
                {currentStep < 4 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!isStepValid()}
                  >
                    Siguiente
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={save}
                    disabled={!isStepValid() || saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
